<template>
  <nav class="navbar" :class="{ 'scrolled': isScrolled }">
    <div class="navbar-container">
      <!-- Logo -->
      <router-link to="/" class="navbar-brand">VITRAIL-A</router-link>

      <!-- Mobile Toggle -->
      <button class="navbar-toggler" @click="mobileMenuOpen = !mobileMenuOpen">
        <span></span>
        <span></span>
        <span></span>
      </button>

      <!-- Nav Items -->
      <div class="navbar-menu" :class="{ 'is-active': mobileMenuOpen }">
        
        <!-- Guest Navigation -->
        <template v-if="!userRole">
          <router-link to="/" class="navbar-item" @click="closeMobile">Accueil</router-link>
          <!-- Marketplace Dropdown -->
          <div class="navbar-item has-dropdown" :class="{ 'is-active': activeDropdown === 'marketplace' }">
            <a class="navbar-link" @click="toggle('marketplace')">
              Marketplace
              <i class="fas fa-chevron-down"></i>
            </a>
            <div class="navbar-dropdown">
              <router-link to="/chevaux-en-vente" class="dropdown-item" @click="closeAll">Chevaux en vente</router-link>
              <router-link to="/animaux-en-vente" class="dropdown-item" @click="closeAll">Animaux en vente</router-link>
            </div>
          </div>

          <div class="navbar-spacer"></div>

          <router-link to="/demande-pro-sante" class="navbar-item btn-secondary" @click="closeMobile">S'inscrire vétérinaire</router-link>
          <router-link to="/inscription" class="navbar-item btn-secondary" @click="closeMobile">S'inscrire</router-link>
          <router-link to="/connexion" class="navbar-item btn-primary" @click="closeMobile">Connexion</router-link>
        </template>

        <!-- Default User Navigation -->
        <template v-else-if="userRole === 'Default'">
          <!-- Mes animaux Dropdown -->
          <div class="navbar-item has-dropdown" :class="{ 'is-active': activeDropdown === 'animals' }">
            <a class="navbar-link" @click="toggle('animals')">
              Mes animaux
              <i class="fas fa-chevron-down"></i>
            </a>
            <div class="navbar-dropdown">
              <router-link to="/mes-chevaux" class="dropdown-item" @click="closeAll">Mes chevaux</router-link>
              <router-link to="/mes-animaux" class="dropdown-item" @click="closeAll">Mes animaux de compagnie</router-link>
              <hr class="dropdown-divider">
              <router-link to="/ajouter-un-cheval" class="dropdown-item" @click="closeAll">Ajouter un cheval</router-link>
              <router-link to="/ajouter-un-animal" class="dropdown-item" @click="closeAll">Ajouter un animal</router-link>
            </div>
          </div>

          <!-- Marketplace Dropdown -->
          <div class="navbar-item has-dropdown" :class="{ 'is-active': activeDropdown === 'marketplace' }">
            <a class="navbar-link" @click="toggle('marketplace')">
              Marketplace
              <i class="fas fa-chevron-down"></i>
            </a>
            <div class="navbar-dropdown">
              <router-link to="/chevaux-en-vente" class="dropdown-item" @click="closeAll">Chevaux en vente</router-link>
              <router-link to="/animaux-en-vente" class="dropdown-item" @click="closeAll">Animaux en vente</router-link>
              <hr class="dropdown-divider">
              <router-link to="/vendre-un-animal" class="dropdown-item" @click="closeAll">
                <i class="fas fa-tag"></i> Vendre un animal
              </router-link>
            </div>
          </div>

          <router-link to="/transferts" class="navbar-item" @click="closeMobile">Transferts</router-link>

          <div class="navbar-spacer"></div>

          <router-link to="/mon-panier" class="navbar-item cart-link" @click="closeMobile">
            <i class="fas fa-shopping-cart"></i>
            <span v-if="cartCount > 0" class="cart-badge">{{ cartCount > 99 ? '99+' : cartCount }}</span>
          </router-link>

          <!-- Account Dropdown -->
          <div class="navbar-item has-dropdown" :class="{ 'is-active': activeDropdown === 'account' }">
            <a class="navbar-link" @click="toggle('account')">
              Mon compte
              <i class="fas fa-chevron-down"></i>
            </a>
            <div class="navbar-dropdown is-right">
              <router-link to="/mon-compte" class="dropdown-item" @click="closeAll">Mon compte</router-link>
              <router-link to="/mon-profil" class="dropdown-item" @click="closeAll">Mon profil</router-link>
              <router-link to="/mes-commandes" class="dropdown-item" @click="closeAll">Mes commandes</router-link>
              <router-link to="/mes-abonnements" class="dropdown-item" @click="closeAll">Mes abonnements</router-link>
              <router-link to="/mes-adresses" class="dropdown-item" @click="closeAll">Mes adresses</router-link>
              <router-link to="/moyens-paiement" class="dropdown-item" @click="closeAll">Moyens de paiement</router-link>
              <hr class="dropdown-divider">
              <router-link to="/changement-de-proprietaire" class="dropdown-item" @click="closeAll">Transfert de propriété</router-link>
              <router-link to="/nous-contacter" class="dropdown-item" @click="closeAll">Nous contacter</router-link>
              <router-link to="/mentions-legales" class="dropdown-item" @click="closeAll">Mentions légales</router-link>
              <hr class="dropdown-divider">
              <a class="dropdown-item text-danger" data-testid="nav-logout" @click="logout">Déconnexion</a>
            </div>
          </div>
        </template>

        <!-- Veto Navigation -->
        <template v-else-if="userRole === 'Veto'">
          <!-- Search Dropdown -->
          <div class="navbar-item has-dropdown" :class="{ 'is-active': activeDropdown === 'search' }">
            <a class="navbar-link" @click="toggle('search')">
              Rechercher
              <i class="fas fa-chevron-down"></i>
            </a>
            <div class="navbar-dropdown">
              <router-link to="/recherche-cheval" class="dropdown-item" @click="closeAll">Rechercher un cheval</router-link>
              <router-link to="/recherche-animal" class="dropdown-item" @click="closeAll">Rechercher un animal</router-link>
            </div>
          </div>

          <router-link to="/mon-profil" class="navbar-item" @click="closeMobile">Mon cabinet</router-link>
          <router-link to="/mon-compte" class="navbar-item" @click="closeMobile">Mon compte</router-link>

          <div class="navbar-spacer"></div>

          <!-- Help Dropdown -->
          <div class="navbar-item has-dropdown" :class="{ 'is-active': activeDropdown === 'help' }">
            <a class="navbar-link" @click="toggle('help')">
              <i class="fas fa-question-circle"></i>
            </a>
            <div class="navbar-dropdown is-right">
              <router-link to="/nous-contacter" class="dropdown-item" @click="closeAll">Nous contacter</router-link>
              <router-link to="/mentions-legales" class="dropdown-item" @click="closeAll">Mentions légales</router-link>
            </div>
          </div>

          <a class="navbar-item btn-danger" data-testid="nav-logout" @click="logout">Déconnexion</a>
        </template>

        <!-- Admin Navigation -->
        <template v-else-if="userRole === 'Admin'">
          <router-link to="/admin" class="navbar-item" @click="closeMobile">Dashboard</router-link>

          <!-- Data Dropdown -->
          <div class="navbar-item has-dropdown" :class="{ 'is-active': activeDropdown === 'data' }">
            <a class="navbar-link" @click="toggle('data')">
              Données
              <i class="fas fa-chevron-down"></i>
            </a>
            <div class="navbar-dropdown">
              <router-link to="/admin/users" class="dropdown-item" @click="closeAll">Utilisateurs</router-link>
              <router-link to="/admin/horses" class="dropdown-item" @click="closeAll">Chevaux</router-link>
              <router-link to="/admin/animals" class="dropdown-item" @click="closeAll">Animaux</router-link>
              <router-link to="/admin/treatments" class="dropdown-item" @click="closeAll">Traitements</router-link>
            </div>
          </div>

          <!-- Management Dropdown -->
          <div class="navbar-item has-dropdown" :class="{ 'is-active': activeDropdown === 'management' }">
            <a class="navbar-link" @click="toggle('management')">
              Gestion
              <i class="fas fa-chevron-down"></i>
            </a>
            <div class="navbar-dropdown">
              <router-link to="/admin/orders" class="dropdown-item" @click="closeAll">Commandes</router-link>
              <router-link to="/admin/listings" class="dropdown-item" @click="closeAll">Annonces</router-link>
              <router-link to="/admin/veto-requests" class="dropdown-item" @click="closeAll">Demandes Véto</router-link>
              <hr class="dropdown-divider">
              <router-link to="/admin/print-monitor" class="dropdown-item" @click="closeAll">Moniteur d'impression</router-link>
              <router-link to="/admin/audit" class="dropdown-item" @click="closeAll">Journal d'audit</router-link>
            </div>
          </div>

          <div class="navbar-spacer"></div>

          <a class="navbar-item btn-danger" data-testid="nav-logout" @click="logout">Déconnexion</a>
        </template>

        <!-- Fallback -->
        <template v-else>
          <router-link to="/" class="navbar-item" @click="closeMobile">Accueil</router-link>
          <router-link to="/connexion" class="navbar-item btn-primary" @click="closeMobile">Connexion</router-link>
        </template>
      </div>
    </div>
  </nav>
</template>

<script>
export default {
  name: 'NavigationBar',
  data() {
    return {
      isScrolled: false,
      mobileMenuOpen: false,
      activeDropdown: null
    }
  },
  computed: {
    userRole() {
      return this.$store.getters.role
    },
    cartCount() {
      return this.$store.getters.cartCount || 0
    }
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll)
    document.addEventListener('click', this.onClickOutside)
    this.loadCartCount()
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.onScroll)
    document.removeEventListener('click', this.onClickOutside)
  },
  methods: {
    onScroll() {
      this.isScrolled = window.scrollY > 20
    },
    toggle(name) {
      this.activeDropdown = this.activeDropdown === name ? null : name
    },
    closeAll() {
      this.activeDropdown = null
      this.mobileMenuOpen = false
    },
    closeMobile() {
      this.mobileMenuOpen = false
    },
    onClickOutside(e) {
      if (!e.target.closest('.has-dropdown')) {
        this.activeDropdown = null
      }
      if (this.mobileMenuOpen && !e.target.closest('.navbar-menu') && !e.target.closest('.navbar-toggler')) {
        this.mobileMenuOpen = false
      }
    },
    async logout() {
      const { useAuthStore } = await import('@/stores/auth')
      const authStore = useAuthStore()
      authStore.logout()
      this.closeAll()
    },
    async loadCartCount() {
      if (this.userRole === 'Default') {
        const { useAuthStore } = await import('@/stores/auth')
        const authStore = useAuthStore()
        authStore.fetchCartCount()
      }
    }
  }
}
</script>

<style scoped>
/* Glass Navbar Base */
.navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 9999;
  padding: 0.75rem 1.5rem;
  background: linear-gradient(135deg, rgba(var(--theme-primary-rgb), 0.15) 0%, rgba(var(--theme-primary-rgb), 0.1) 100%);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  border-bottom: 1px solid var(--theme-border-color);
  box-shadow: 
    0 4px 30px rgba(var(--theme-primary-rgb), 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.4);
  transition: all 0.3s ease;
}

.navbar.scrolled {
  background: linear-gradient(135deg, rgba(var(--theme-primary-rgb), 0.25) 0%, rgba(var(--theme-primary-rgb), 0.2) 100%);
  box-shadow: 
    0 8px 32px rgba(var(--theme-primary-rgb), 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
}

.navbar-container {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

/* Brand */
.navbar-brand {
  font-size: 1.5rem;
  font-weight: 700;
  background: linear-gradient(135deg, var(--theme-primary) 0%, var(--theme-primary-dark) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-decoration: none;
}

/* Mobile Toggler */
.navbar-toggler {
  display: none;
  flex-direction: column;
  gap: 5px;
  padding: 0.5rem;
  background: none;
  border: none;
  cursor: pointer;
}

.navbar-toggler span {
  width: 24px;
  height: 2px;
  background: var(--theme-primary);
  border-radius: 2px;
  transition: all 0.3s ease;
}

/* Menu */
.navbar-menu {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.navbar-spacer {
  flex: 1;
  min-width: 2rem;
}

/* Nav Items */
.navbar-item {
  padding: 0.5rem 1rem;
  color: var(--theme-primary-dark);
  text-decoration: none;
  border-radius: 10px;
  font-weight: 500;
  transition: all 0.2s;
  white-space: nowrap;
}

.navbar-item:hover {
  background: var(--theme-hover-bg);
  color: var(--theme-primary-dark);
}

/* Dropdown Container */
.has-dropdown {
  position: relative;
  padding: 0;
}

.navbar-link {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  color: var(--theme-primary-dark);
  cursor: pointer;
  border-radius: 10px;
  font-weight: 500;
  transition: all 0.2s;
}

.navbar-link:hover {
  background: var(--theme-hover-bg);
  color: var(--theme-primary-dark);
}

.navbar-link i {
  font-size: 0.75rem;
  transition: transform 0.2s;
}

.has-dropdown.is-active .navbar-link i {
  transform: rotate(180deg);
}

/* Dropdown Menu - Glass Style */
.navbar-dropdown {
  display: none;
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 0;
  min-width: 220px;
  padding: 0.5rem;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, var(--theme-bg-start) 100%);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  border: 1px solid var(--theme-border-color);
  border-radius: 14px;
  box-shadow: 
    0 10px 40px rgba(var(--theme-primary-rgb), 0.15),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
  z-index: 100;
}

.navbar-dropdown.is-right {
  left: auto;
  right: 0;
}

.has-dropdown.is-active .navbar-dropdown {
  display: block;
}

.dropdown-item {
  display: block;
  padding: 0.625rem 1rem;
  color: var(--theme-primary-dark);
  text-decoration: none;
  border-radius: 8px;
  font-size: 0.9rem;
  transition: all 0.2s;
}

.dropdown-item:hover {
  background: var(--theme-hover-bg);
  color: var(--theme-primary-dark);
}

.dropdown-item.text-danger {
  color: #dc3545;
}

.dropdown-item.text-danger:hover {
  background: rgba(220, 53, 69, 0.1);
}

.dropdown-divider {
  margin: 0.5rem 0;
  border: none;
  border-top: 1px solid var(--theme-border-color);
}

/* Buttons */
.btn-primary {
  background: linear-gradient(135deg, var(--theme-primary), var(--theme-primary-dark));
  color: white !important;
  box-shadow: 0 4px 15px rgba(var(--theme-primary-rgb), 0.3);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(var(--theme-primary-rgb), 0.4);
  background: linear-gradient(135deg, var(--theme-primary), var(--theme-primary-dark));
  color: white !important;
}

.btn-secondary {
  background: rgba(var(--theme-primary-rgb), 0.15);
  color: var(--theme-primary-dark) !important;
  border: 1px solid rgba(var(--theme-primary-rgb), 0.3);
}

.btn-secondary:hover {
  background: rgba(var(--theme-primary-rgb), 0.25);
}

.btn-danger {
  background: rgba(220, 53, 69, 0.1);
  color: #dc3545 !important;
  border: 1px solid rgba(220, 53, 69, 0.2);
}

.btn-danger:hover {
  background: rgba(220, 53, 69, 0.2);
}

/* Mobile Responsive */
@media (max-width: 1024px) {
  .navbar-toggler {
    display: flex;
  }

  .navbar-menu {
    display: none;
    position: fixed;
    top: 0;
    right: 0;
    width: 300px;
    height: 100vh;
    flex-direction: column;
    align-items: stretch;
    padding: 4rem 1rem 1rem;
    background: linear-gradient(180deg, var(--theme-bg-start) 0%, rgba(255, 255, 255, 0.95) 100%);
    backdrop-filter: blur(3px);
    -webkit-backdrop-filter: blur(3px);
    box-shadow: -5px 0 30px rgba(var(--theme-primary-rgb), 0.15);
    overflow-y: auto;
  }

  .navbar-menu.is-active {
    display: flex;
  }

  .navbar-spacer {
    display: none;
  }

  .has-dropdown {
    flex-direction: column;
  }

  .navbar-dropdown {
    position: static;
    box-shadow: none;
    border: none;
    background: rgba(var(--theme-primary-rgb), 0.08);
    margin-top: 0.25rem;
    padding: 0.25rem 0 0.25rem 1rem;
  }

  .navbar-dropdown.is-right {
    left: 0;
    right: auto;
  }
}

/* Cart badge */
.cart-link {
  position: relative;
}

.cart-badge {
  position: absolute;
  top: -6px;
  right: -8px;
  background: var(--color-danger);
  color: white;
  font-size: 0.65rem;
  font-weight: 700;
  min-width: 18px;
  height: 18px;
  line-height: 18px;
  text-align: center;
  border-radius: 9px;
  padding: 0 4px;
  pointer-events: none;
}
</style>
